import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import Badge from '../components/ui/Badge';
import { fetchDriverStripeOnboardingLink, getAdminDrivers } from '../store/thunks/driverActions';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@mui/material';

const DRIVER_LIST_STATE_KEY = 'bellhopt.admin.driverList.v1';

/**
 * Redesigned driver management screen backed by /api/v1/admin/drivers.
 * Provides quick-filter chips for the operational states we care about
 * (active, compliance needed, inactive, restricted, below 3.0) while
 * keeping search + market filtering against the admin endpoint.
 */

const FILTER_CHIPS = [
  { id: 'all', label: 'All Drivers' },
  { id: 'active', label: 'Active' },
  { id: 'compliance_needed', label: 'Compliance Needed' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'restricted', label: 'Restricted' },
  { id: 'below_rating', label: 'Below 3.0' },
];

const SORT_OPTIONS = [
  { id: 'recent', label: 'Recent (last active)' },
  { id: 'lastActive-asc', label: 'Least recent' },
  { id: 'newest', label: 'Newest sign up' },
  { id: 'oldest', label: 'Oldest sign up' },
  { id: 'name-asc', label: 'Name A–Z' },
  { id: 'name-desc', label: 'Name Z–A' },
  { id: 'orders-desc', label: 'Most orders' },
  { id: 'orders-asc', label: 'Fewest orders' },
  { id: 'rating-desc', label: 'Highest rating' },
  { id: 'rating-asc', label: 'Lowest rating' },
];

const FILTER_IDS = new Set(FILTER_CHIPS.map((chip) => chip.id));
const SORT_IDS = new Set(SORT_OPTIONS.map((option) => option.id));

const readDriverListState = () => {
  try {
    const raw = sessionStorage.getItem(DRIVER_LIST_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return {
      filter: FILTER_IDS.has(parsed.filter) ? parsed.filter : undefined,
      search: typeof parsed.search === 'string' ? parsed.search.slice(0, 200) : undefined,
      market: typeof parsed.market === 'string' ? parsed.market.slice(0, 80) : undefined,
      minOrders: typeof parsed.minOrders === 'string' ? parsed.minOrders.slice(0, 8) : undefined,
      maxOrders: typeof parsed.maxOrders === 'string' ? parsed.maxOrders.slice(0, 8) : undefined,
      sort: SORT_IDS.has(parsed.sort) ? parsed.sort : undefined,
      scrollTop: Number.isFinite(Number(parsed.scrollTop))
        ? Math.max(0, Number(parsed.scrollTop))
        : 0,
      pageScrollTop: Number.isFinite(Number(parsed.pageScrollTop))
        ? Math.max(0, Number(parsed.pageScrollTop))
        : 0,
    };
  } catch {
    return {};
  }
};

const toTimestamp = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const copyToClipboard = async (value) => {
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};

const complianceVariant = {
  ready: 'success',
  pending: 'warning',
  rejected: 'danger',
  missing: 'default',
};

const complianceLabel = {
  ready: 'Ready',
  pending: 'Pending Review',
  rejected: 'Rejected',
  missing: 'Missing',
};

const CHECKR_CLEARED_STATUSES = ['clear', 'cleared'];
const CHECKR_RESTRICTED_STATUSES = ['consider', 'cancelled'];

const STATUS_META = {
  active: { label: 'Active', variant: 'success' },
  compliance_needed: { label: 'Compliance Needed', variant: 'warning' },
  inactive: { label: 'Inactive', variant: 'default' },
  restricted: { label: 'Restricted', variant: 'danger' },
};

const isTruthyValue = (value) => {
  if (value === true) return true;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    return ['true', 'yes', 'approved', 'accepted', 'signed', 'complete', 'completed', 'ready'].includes(
      value.trim().toLowerCase()
    );
  }
  return false;
};

const hasUsableDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

const getComplianceDocStatus = (doc) => {
  if (!doc) return 'missing';
  if (typeof doc === 'string') return doc.trim().toLowerCase();
  return String(doc.status || doc.state || '').trim().toLowerCase() || 'missing';
};

const getStandardsAccepted = (driver) => {
  const explicitFields = [
    driver?.standardsAccepted,
    driver?.standardsSigned,
    driver?.acceptedStandards,
    driver?.acknowledgmentAccepted,
    driver?.agreementAccepted,
    driver?.standardsAcceptedAt,
    driver?.standardsSignedAt,
    driver?.policyAcknowledgedAt,
    driver?.handbookAcknowledgedAt,
  ];

  const hasExplicitValue = explicitFields.some((value) => value !== undefined && value !== null && value !== '');
  const explicitAccepted = explicitFields.some((value) => isTruthyValue(value) || hasUsableDate(value));

  if (hasExplicitValue) return explicitAccepted;
  return isTruthyValue(driver?.isOnBoarded);
};

const getDriverState = (driver) => {
  const isSuspended = driver?.suspendedUntil && new Date(driver.suspendedUntil) > new Date();
  const manualInactive = driver?.isActive === false;
  const checkr = String(driver?.checkrVerified || 'pending').trim().toLowerCase();
  const checkrCleared = CHECKR_CLEARED_STATUSES.includes(checkr);
  const checkrRestricted = CHECKR_RESTRICTED_STATUSES.includes(checkr);

  const overallComplianceState = String(driver?.compliance?.state || '').trim().toLowerCase();
  const hasDocBreakdown = Boolean(driver?.compliance?.photoId || driver?.compliance?.selfie);

  const photoStatus = hasDocBreakdown
    ? getComplianceDocStatus(driver?.compliance?.photoId)
    : overallComplianceState === 'ready'
      ? 'approved'
      : overallComplianceState === 'rejected'
        ? 'rejected'
        : overallComplianceState || 'missing';
  const selfieStatus = hasDocBreakdown
    ? getComplianceDocStatus(driver?.compliance?.selfie)
    : overallComplianceState === 'ready'
      ? 'approved'
      : overallComplianceState === 'rejected'
        ? 'rejected'
        : overallComplianceState || 'missing';
  const photoApproved = photoStatus === 'approved';
  const selfieApproved = selfieStatus === 'approved';
  const docRejected = photoStatus === 'rejected' || selfieStatus === 'rejected';
  const standardsAccepted = getStandardsAccepted(driver);

  const complianceReasons = [];
  if (!photoApproved) {
    complianceReasons.push(
      photoStatus === 'pending'
        ? 'Government ID pending approval'
        : photoStatus === 'rejected'
          ? 'Government ID rejected'
          : 'Government ID missing'
    );
  }
  if (!selfieApproved) {
    complianceReasons.push(
      selfieStatus === 'pending'
        ? 'Selfie pending approval'
        : selfieStatus === 'rejected'
          ? 'Selfie rejected'
          : 'Selfie missing'
    );
  }
  if (!standardsAccepted) {
    complianceReasons.push('Standards not signed');
  }
  if (!checkrCleared) {
    complianceReasons.push(
      checkrRestricted ? `Checkr ${checkr}` : 'Checkr pending'
    );
  }

  let status = 'active';
  if (isSuspended || docRejected || checkrRestricted) {
    status = 'restricted';
  } else if (manualInactive) {
    status = 'inactive';
  } else if (complianceReasons.length > 0) {
    status = 'compliance_needed';
  }

  let complianceState = 'ready';
  if (docRejected) {
    complianceState = 'rejected';
  } else if (!photoApproved || !selfieApproved || !standardsAccepted) {
    complianceState = 'pending';
  }

  return {
    status,
    checkr,
    complianceState,
    complianceReasons,
  };
};

function RatingStar({ value }) {
  const rounded = Number(value ?? 5).toFixed(1);
  const tone = value < 3 ? 'text-red-600' : value < 4 ? 'text-amber-600' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M10 15.27 16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
      </svg>
      {rounded}
    </span>
  );
}

function SortableHeader({ label, column, sort, onSort }) {
  const columnSorts = {
    name: ['name-asc', 'name-desc'],
    lastActive: ['recent', 'lastActive-asc'],
    signup: ['newest', 'oldest'],
    orders: ['orders-desc', 'orders-asc'],
    rating: ['rating-desc', 'rating-asc'],
  };
  const [primary, secondary] = columnSorts[column] || [];
  const active = sort === primary || sort === secondary;
  const ascending = sort === secondary || sort === 'name-asc' || sort === 'oldest' || sort === 'lastActive-asc' || sort === 'orders-asc' || sort === 'rating-asc';

  return (
    <th className="text-left px-5 py-3 font-semibold">
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          active ? 'text-[#4F46E5]' : 'text-[#71717A]'
        }`}
      >
        {label}
        <span className="text-[10px]">{active ? (ascending ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}

function DriverListScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const savedState = useMemo(() => readDriverListState(), []);
  const pageRef = useRef(null);
  const scrollRef = useRef(null);
  const persistRef = useRef({});
  const didRestoreScroll = useRef(false);

  const [filter, setFilter] = useState(savedState.filter || 'all');
  const [search, setSearch] = useState(savedState.search || '');
  const [market, setMarket] = useState(savedState.market || '');
  const [minOrders, setMinOrders] = useState(savedState.minOrders || '');
  const [maxOrders, setMaxOrders] = useState(savedState.maxOrders || '');
  const [sort, setSort] = useState(savedState.sort || 'recent');
  const [stripeAction, setStripeAction] = useState({});

  const { adminDriversLoading, adminDrivers, adminDriversError } = useSelector(
    (state) => state.allDrivers ?? {}
  );

  persistRef.current = { filter, search, market, minOrders, maxOrders, sort };

  const persistListState = () => {
    try {
      sessionStorage.setItem(
        DRIVER_LIST_STATE_KEY,
        JSON.stringify({
          ...persistRef.current,
          scrollTop: scrollRef.current?.scrollTop ?? persistRef.current.scrollTop ?? 0,
          pageScrollTop: pageRef.current?.scrollTop ?? persistRef.current.pageScrollTop ?? 0,
        })
      );
    } catch {
      /* ignore quota / private mode */
    }
  };

  useEffect(() => {
    const params = { limit: 'all' };
    if (search.trim()) params.search = search.trim();
    if (market.trim()) params.market = market.trim();
    const timer = setTimeout(() => {
      dispatch(getAdminDrivers(params));
    }, 200);
    return () => clearTimeout(timer);
  }, [dispatch, search, market]);

  useEffect(() => {
    const listEl = scrollRef.current;
    const pageEl = pageRef.current;
    const onScroll = () => {
      persistRef.current.scrollTop = listEl?.scrollTop || 0;
      persistRef.current.pageScrollTop = pageEl?.scrollTop || 0;
    };
    listEl?.addEventListener('scroll', onScroll, { passive: true });
    pageEl?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      listEl?.removeEventListener('scroll', onScroll);
      pageEl?.removeEventListener('scroll', onScroll);
      persistListState();
    };
  }, []);

  const drivers = useMemo(() => adminDrivers?.drivers ?? [], [adminDrivers]);
  const filteredDrivers = useMemo(() => {
    const minOrdersValue = minOrders === '' ? null : Number(minOrders);
    const maxOrdersValue = maxOrders === '' ? null : Number(maxOrders);

    return drivers.filter((driver) => {
      const state = getDriverState(driver);
      const totalOrders = Number(driver?.totalOrders || 0);

      if (minOrdersValue !== null && totalOrders < minOrdersValue) return false;
      if (maxOrdersValue !== null && totalOrders > maxOrdersValue) return false;
      if (filter === 'all') return true;
      if (filter === 'below_rating') return Number(driver?.rating ?? 5) < 3;
      return state.status === filter;
    });
  }, [drivers, filter, minOrders, maxOrders]);

  const sortedDrivers = useMemo(() => {
    const list = [...filteredDrivers];
    const nameOf = (driver) => `${driver.firstName || ''} ${driver.lastName || ''}`.trim().toLowerCase();

    switch (sort) {
      case 'recent':
        return list.sort(
          (a, b) =>
            toTimestamp(b.lastOrderAt) - toTimestamp(a.lastOrderAt) ||
            toTimestamp(b.createdAt) - toTimestamp(a.createdAt)
        );
      case 'lastActive-asc':
        return list.sort(
          (a, b) =>
            toTimestamp(a.lastOrderAt) - toTimestamp(b.lastOrderAt) ||
            toTimestamp(a.createdAt) - toTimestamp(b.createdAt)
        );
      case 'newest':
        return list.sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt));
      case 'oldest':
        return list.sort((a, b) => toTimestamp(a.createdAt) - toTimestamp(b.createdAt));
      case 'name-asc':
        return list.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
      case 'name-desc':
        return list.sort((a, b) => nameOf(b).localeCompare(nameOf(a)));
      case 'orders-desc':
        return list.sort((a, b) => Number(b.totalOrders || 0) - Number(a.totalOrders || 0));
      case 'orders-asc':
        return list.sort((a, b) => Number(a.totalOrders || 0) - Number(b.totalOrders || 0));
      case 'rating-desc':
        return list.sort((a, b) => Number(b.rating ?? 5) - Number(a.rating ?? 5));
      case 'rating-asc':
        return list.sort((a, b) => Number(a.rating ?? 5) - Number(b.rating ?? 5));
      default:
        return list;
    }
  }, [filteredDrivers, sort]);

  useEffect(() => {
    if (didRestoreScroll.current) return;
    if (adminDriversLoading && sortedDrivers.length === 0) return;
    const listEl = scrollRef.current;
    const pageEl = pageRef.current;
    if (!listEl && !pageEl) return;
    requestAnimationFrame(() => {
      if (listEl) listEl.scrollTop = savedState.scrollTop || 0;
      if (pageEl) pageEl.scrollTop = savedState.pageScrollTop || 0;
      didRestoreScroll.current = true;
    });
  }, [adminDriversLoading, savedState.scrollTop, sortedDrivers.length]);

  const toggleColumnSort = (column) => {
    const columnSorts = {
      name: ['name-asc', 'name-desc'],
      lastActive: ['recent', 'lastActive-asc'],
      signup: ['newest', 'oldest'],
      orders: ['orders-desc', 'orders-asc'],
      rating: ['rating-desc', 'rating-asc'],
    };
    const [primary, secondary] = columnSorts[column] || [];
    if (!primary) return;
    setSort((current) => (current === primary ? secondary : primary));
  };

  const updateStripeAction = (driverId, patch) => {
    setStripeAction((current) => ({
      ...current,
      [driverId]: { ...(current[driverId] || {}), ...patch },
    }));
  };

  const copyStripeAccountId = async (event, driver) => {
    event.stopPropagation();
    const driverId = driver._id || driver.id;
    const copied = await copyToClipboard(driver.stripeAccountID);
    updateStripeAction(driverId, {
      accountCopied: copied,
      error: copied ? '' : 'Could not copy account id',
    });
    if (copied) {
      setTimeout(() => updateStripeAction(driverId, { accountCopied: false }), 1600);
    }
  };

  const copyOnboardingLink = async (event, driver) => {
    event.stopPropagation();
    const driverId = driver._id || driver.id;
    if (!driverId) return;
    updateStripeAction(driverId, { loading: true, error: '', copied: false });
    try {
      const data = await fetchDriverStripeOnboardingLink(driverId);
      const copied = await copyToClipboard(data?.url);
      updateStripeAction(driverId, {
        loading: false,
        url: data?.url || '',
        stripeAccountID: data?.stripeAccountID || driver.stripeAccountID || '',
        copied,
        error: copied ? '' : 'Link ready, but clipboard copy failed',
      });
      if (copied) {
        setTimeout(() => updateStripeAction(driverId, { copied: false }), 1600);
      }
    } catch (err) {
      updateStripeAction(driverId, {
        loading: false,
        error: err?.response?.data?.message || 'Could not create onboarding link',
      });
    }
  };

  return (
    <div ref={pageRef} className="flex w-full h-screen overflow-auto">
      {adminDriversError && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', width: '90%', margin: 'auto', top: 20, left: 0, right: 0, zIndex: 10 }}
        >
          <strong>{adminDriversError?.message || 'Could not load drivers.'}</strong>
        </Alert>
      )}

      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader heading={'Drivers'} pages={'Pages / Drivers'} />
        </div>

        <div ref={scrollRef} className="px-6 py-5 h-screen overflow-auto">
          {/* Filters + search */}
          <div className="bg-white rounded-xl px-5 py-4 mb-4 shadow-sm">
            <div className="flex flex-wrap gap-2 mb-3">
              {FILTER_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  onClick={() => setFilter(chip.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                    filter === chip.id
                      ? 'bg-[#4F46E5] text-white border-[#4F46E5]'
                      : 'bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#A5B4FC]'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <input
                className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Search by name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <input
                className="w-48 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Filter by market"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
              />
              <input
                type="number"
                min="0"
                inputMode="numeric"
                className="w-36 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Min orders"
                value={minOrders}
                onChange={(e) => setMinOrders(e.target.value)}
              />
              <input
                type="number"
                min="0"
                inputMode="numeric"
                className="w-36 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Max orders"
                value={maxOrders}
                onChange={(e) => setMaxOrders(e.target.value)}
              />
              <select
                className="w-52 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5] bg-white"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-3 text-xs text-[#71717A]">
              Showing {sortedDrivers.length} of {drivers.length} drivers
            </div>
          </div>

          {/* Drivers table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#71717A] text-xs uppercase tracking-wide">
                  <tr>
                    <SortableHeader label="Driver" column="name" sort={sort} onSort={toggleColumnSort} />
                    <th className="text-left px-5 py-3 font-semibold">Contact</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                    <SortableHeader label="Rating" column="rating" sort={sort} onSort={toggleColumnSort} />
                    <th className="text-left px-5 py-3 font-semibold">Compliance</th>
                    <th className="text-left px-5 py-3 font-semibold">Checkr</th>
                    <th className="text-left px-5 py-3 font-semibold">Market</th>
                    <SortableHeader label="Orders" column="orders" sort={sort} onSort={toggleColumnSort} />
                    <SortableHeader label="Last Active" column="lastActive" sort={sort} onSort={toggleColumnSort} />
                    <SortableHeader label="Sign Up" column="signup" sort={sort} onSort={toggleColumnSort} />
                    <th className="text-left px-5 py-3 font-semibold">Stripe</th>
                    <th className="text-right px-5 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminDriversLoading && (
                    <tr>
                      <td colSpan={12} className="text-center py-10 text-[#71717A]">
                        Loading drivers…
                      </td>
                    </tr>
                  )}
                  {!adminDriversLoading && sortedDrivers.length === 0 && (
                    <tr>
                      <td colSpan={12} className="text-center py-10 text-[#71717A]">
                        No drivers match the current filters.
                      </td>
                    </tr>
                  )}
                  {sortedDrivers.map((d) => {
                    const state = getDriverState(d);
                    const meta = STATUS_META[state.status] || STATUS_META.inactive;

                    const checkr = state.checkr || 'pending';
                    const CHECKR_META = {
                      clear: { label: 'Cleared', variant: 'success' },
                      cleared: { label: 'Cleared', variant: 'success' },
                      pending: { label: 'Pending', variant: 'default' },
                      consider: { label: 'Consider', variant: 'warning' },
                      cancelled: { label: 'Cancelled', variant: 'danger' },
                      created: { label: 'Created', variant: 'default' },
                    };
                    const checkrMeta = CHECKR_META[checkr] || CHECKR_META.pending;

                    const driverId = d._id || d.id;
                    const stripeState = stripeAction[driverId] || {};
                    const stripeAccountId = stripeState.stripeAccountID || d.stripeAccountID;
                    const openDriver = () => {
                      if (!driverId) return;
                      persistListState();
                      navigate(`/driverList/${driverId}`);
                    };

                    return (
                      <tr
                        key={driverId || d.email}
                        onClick={openDriver}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDriver();
                          }
                        }}
                        tabIndex={driverId ? 0 : -1}
                        role="button"
                        className={`
                          border-t border-[#F4F4F5] transition-colors
                          ${driverId ? 'cursor-pointer hover:bg-[#FAFAFA] focus:bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#4F46E5]/30' : ''}
                        `}
                        title={driverId ? `Open ${d.firstName || ''} ${d.lastName || ''}`.trim() : undefined}
                      >
                        {/* Driver */}
                        <td className="px-5 py-4">
                          <div className="font-medium text-[#18181B] hover:text-[#4F46E5]">
                            {d.firstName} {d.lastName}
                          </div>
                          <div className="text-[10px] font-mono text-[#A1A1AA] mt-0.5">
                            {String(d._id || '').slice(-6)}
                          </div>
                        </td>

                        {/* Contact — clickable mailto / tel so ops can
                            reach the driver in one click. */}
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-0.5">
                            {d.email ? (
                              <a
                                href={`mailto:${d.email}`}
                                title={d.email}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-[#4F46E5] hover:underline truncate max-w-[220px]"
                              >
                                {d.email}
                              </a>
                            ) : (
                              <span className="text-xs text-[#A1A1AA]">no email</span>
                            )}
                            {d.phoneNumber ? (
                              <a
                                href={`tel:${d.phoneNumber}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-[#3F3F46] hover:text-[#4F46E5] hover:underline"
                              >
                                {d.phoneNumber}
                              </a>
                            ) : (
                              <span className="text-xs text-[#A1A1AA]">no phone</span>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4">
                          <Badge variant={meta.variant} dot>
                            {meta.label}
                          </Badge>
                          {state.complianceReasons.length > 0 && (
                            <div className="mt-1 text-[10px] text-[#B91C1C] uppercase tracking-wide">
                              {state.complianceReasons[0]}
                            </div>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <RatingStar value={d.rating} />
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant={complianceVariant[state.complianceState] || 'default'}>
                            {complianceLabel[state.complianceState] || 'Missing'}
                          </Badge>
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant={checkrMeta.variant}>{checkrMeta.label}</Badge>
                        </td>
                        <td className="px-5 py-4 text-[#3F3F46]">{d.market || '—'}</td>
                        <td className="px-5 py-4 text-[#3F3F46]">{d.totalOrders || 0}</td>
                        <td className="px-5 py-4 text-[#71717A] text-xs">
                          {d.lastOrderAt ? new Date(d.lastOrderAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-4 text-[#71717A] text-xs whitespace-nowrap">
                          {d.createdAt
                            ? new Date(d.createdAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </td>
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="min-w-[220px] space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <span
                                className="font-mono text-[11px] text-[#3F3F46] truncate max-w-[150px]"
                                title={stripeAccountId || 'No Stripe account'}
                              >
                                {stripeAccountId || 'No account'}
                              </span>
                              {stripeAccountId ? (
                                <button
                                  type="button"
                                  onClick={(e) => copyStripeAccountId(e, d)}
                                  className="text-[10px] font-semibold text-[#4F46E5] hover:underline"
                                >
                                  {stripeState.accountCopied ? 'Copied' : 'Copy ID'}
                                </button>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              disabled={stripeState.loading || !driverId}
                              onClick={(e) => copyOnboardingLink(e, d)}
                              className="text-[11px] font-semibold text-[#4F46E5] hover:underline disabled:opacity-50"
                            >
                              {stripeState.loading
                                ? 'Generating…'
                                : stripeState.copied
                                  ? 'Link copied'
                                  : 'Copy onboarding link'}
                            </button>
                            {stripeState.url ? (
                              <input
                                readOnly
                                value={stripeState.url}
                                onFocus={(e) => e.target.select()}
                                className="w-full px-2 py-1 rounded border border-[#E4E4E7] text-[10px] font-mono text-[#3F3F46] bg-[#FAFAFA]"
                              />
                            ) : null}
                            {stripeState.error ? (
                              <div className="text-[10px] text-[#B91C1C]">{stripeState.error}</div>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            className="bg-[#4F46E5] text-white text-xs px-4 py-1.5 rounded-full hover:bg-[#4338CA]"
                            disabled={!driverId}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDriver();
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverListScreen;
